import { Text } from "@/common/Text";
import SubscriptionService from "@/services/SubscriptionService";
import { Button } from "@/v2-components/ui/button";
import { Checkbox } from "@/v2-components/ui/checkbox";
import { Input } from "@/v2-components/ui/input";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { useSubscribeSchema } from "@/hooks/useSchema";
import InnerHtml from "@/common/InnerHtml";

function ErrorMessage({ message }: { message?: string }) {
  return (
    <span
      className={cn(
        "ml-px flex overflow-hidden text-sm text-red-50",
        message ? "max-h-min opacity-100" : "max-h-0 opacity-0",
      )}
      style={{ transition: "max-height 0.3s ease, opacity 0.3s ease" }}
    >
      {message}
    </span>
  );
}

function EmailForm({ register, errorMessage = "", placeholder = "Email" }) {
  return (
    <div className="relative w-full">
      <Input
        className="h-15 text-base font-400"
        placeholder={placeholder}
        {...register("email")}
      />
      <div className={cn("top-full pt-2")}>
        <ErrorMessage message={errorMessage} />
      </div>
    </div>
  );
}

function Agreement() {
  const { t } = useTrans(["index"]);
  return (
    <div className="text-sm font-400 text-coolgray-60">
      <InnerHtml
        html={t("GDPR", {
          defaultValue: `By checking this box you are agreeing to the bHaptics Inc.<br><a target="_blank" href="/legals/terms-and-conditions" class="underline linkBlue">Terms and Conditions</a> and <a target="_blank" href="/legals/privacy-policy" class="underline linkBlue">Privacy Policy</a>.<br>You may unsubscribe at any time.`,
          first: {
            label: "Terms and Conditions",
            href: "/legals/terms-and-conditions",
          },
          second: { label: "Privacy Policy", href: "/legals/privacy-policy" },
        })}
      />
    </div>
  );
}

function Submit({
  disabled = false,
  text,
  className,
}: {
  disabled?: boolean;
  text: string;
  className?: string;
}) {
  return (
    <Button
      disabled={disabled}
      type={"submit"}
      variant={"blue-outline"}
      className={cn(className, "mt-1 px-6")}
      shape={"pill"}
    >
      <Text className="text-xs font-600" text={text} />
    </Button>
  );
}

function Mobile() {
  const { t } = useTrans(["index"]);
  const schema = useSubscribeSchema();
  type SchemaType = Yup.InferType<typeof schema>;
  const {
    setValue,
    trigger,
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  }: any = useForm<SchemaType>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: "",
      checkbox: false,
    },
  });

  const onSubmit = async (data) => {
    try {
      const { status, errorMessage } = await SubscriptionService.subscribe(
        data.email,
      );
      if (!status && !!errorMessage) {
        return alert(errorMessage);
      }
      alert("subscription email has been successfully sent.");
    } catch (error) {
      console.error(error);
      alert((error as any)?.message ?? "Please retry.");
    }
  };

  return (
    <>
      {/* 피그마 S8(29095:21213): 위 여백은 섹션 간격이 담당하고 이 블록은 아래 64 만 갖는다. */}
      <div className="w-full px-4 pb-16">
        <div className="flex flex-col gap-y-6">
          <div className="flex flex-col gap-y-3">
            <Text
              className="font-500"
              text={t("Keep up to date with bHaptics!")}
            />
            <Text
              className="text-base font-400"
              text={t(
                "Sign up to bHaptics Newsletter for product and content updates, special offers, and news.",
              )}
            />
          </div>
          <div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <EmailForm
                placeholder={t("email", { ns: "gnb" })}
                errorMessage={errors.email?.message}
                register={register}
              />
              <Submit
                // 시안 모바일: 입력 필드와 12px 간격
                className="mt-3 w-full"
                text={t(isSubmitting ? "Submitting..." : "Sign up", {
                  ns: "gnb",
                })}
              />
              <div className="mt-4 flex gap-2">
                <Checkbox
                  id={"check-box-subscribe"}
                  className="border-coolgray-50"
                  {...register("checkbox")}
                  onCheckedChange={(checked) => {
                    setValue("checkbox", checked);
                    trigger("checkbox");
                  }}
                />
                <label htmlFor={"check-box-subscribe"}>
                  <div className="relative">
                    <Agreement />
                    <div className={cn("absolute left-0 top-full pt-1")}>
                      <ErrorMessage message={t(errors?.checkbox?.message)} />
                    </div>
                  </div>
                </label>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

function Desktop() {
  const { t } = useTrans(["index"]);
  const schema = useSubscribeSchema();
  type SchemaType = Yup.InferType<typeof schema>;
  const {
    setValue,
    trigger,
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  }: any = useForm<SchemaType>({
    resolver: yupResolver(schema),
    defaultValues: {
      email: "",
      checkbox: false,
    },
    mode: "all",
  });

  const onSubmit = async (data) => {
    try {
      const { status, errorMessage } = await SubscriptionService.subscribe(
        data.email,
      );
      if (!status && !!errorMessage) {
        return alert(errorMessage);
      }
      alert("subscription email has been successfully sent.");
    } catch (error) {
      console.error(error);
      alert((error as any)?.message ?? "Please retry.");
    }
  };

  return (
    <>
      {/* 피그마 S8 Newsletter(29094:21228): px 16 / pt 0 / pb 64. 위 여백은 섹션 간격이 담당한다. */}
      <div className="w-full px-4 pb-16">
        <div className="mx-auto max-w-screen-max">
          <div className="flex gap-x-6">
            <div className="w-full max-w-[400px] text-coolgray-90">
              <div className="flex flex-col gap-y-4">
                <Text
                  className="text-lg font-500"
                  text={t("Keep up to date with bHaptics!")}
                />
                <Text
                  className="text-base font-400"
                  text={t(
                    "Sign up to bHaptics Newsletter for product and content updates, special offers, and news.",
                  )}
                />
              </div>
            </div>
            <div className="grow">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-y-5">
                  <div className="flex w-full items-start gap-x-6">
                    <EmailForm
                      placeholder={t("email", { ns: "gnb" })}
                      errorMessage={errors.email?.message}
                      register={register}
                    />
                    <Submit
                      text={t(isSubmitting ? "Submitting..." : "Sign up", {
                        ns: "gnb",
                      })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Checkbox
                      id={"check-box-subscribe"}
                      className="border-coolgray-50"
                      {...register("checkbox")}
                      onCheckedChange={(checked) => {
                        setValue("checkbox", checked);
                        trigger("checkbox");
                      }}
                    />
                    <label htmlFor={"check-box-subscribe"}>
                      <div className="relative">
                        <Agreement />
                        <div className={cn("absolute left-0 top-full pt-1")}>
                          <ErrorMessage
                            message={t(errors?.checkbox?.message)}
                          />
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function Subscribe() {
  const isMobile = useIsMobileView();

  return isMobile ? <Mobile /> : <Desktop />;
}
